import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import AuthTransition from "@/components/auth/AuthTransition";
import GoogleLoginButton from "@/components/auth/GoogleLoginButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { User, Building, Mail, Phone, Lock, Eye, EyeOff, Loader2, Zap, Users, TrendingUp, Shield, BarChart3, CheckCircle } from "lucide-react";

// Define the validation schema with trimming and precise mobile/contact number validation
const signupSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Name must be at least 2 characters" })
    .transform((val) => val.trim()),
  email: z
    .string()
    .email({ message: "Please enter a valid email" })
    .min(1, { message: "Email is required" })
    .transform((val) => val.trim()),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
  mobileNo: z
    .string()
    .regex(/^\d{10}$/, { message: "Mobile number must be exactly 10 digits" })
    .transform((val) => val.trim()),
  orgName: z
    .string()
    .min(2, { message: "Organization name must be at least 2 characters" })
    .transform((val) => val.trim())
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SignUp = () => {
  const { signup, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // If user is already authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      mobileNo: "",
      orgName: ""
    },
  });

  // Handler to allow only numeric input
  const handleNumericInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !/[0-9]/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Tab" &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight"
    ) {
      e.preventDefault();
    }
  };

  // Add a function to handle input changes and prevent leading spaces
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const value = e.target.value;
    // Remove leading spaces
    const trimmedValue = value.replace(/^\s+/, "");
    onChange(trimmedValue);
  };

  const onSubmit = async (data: SignupFormValues) => {
    setIsLoading(true);

    try {
      const response = await signup({
        user: {
          name: data.name,
          email: data.email,
          password: data.password,
          mobileNo: data.mobileNo,
          role: "owner",
          isAuthenticated: true,
        },
        organization: {
          name: data.orgName
        },
      });

      console.log("Signup response:", response);

      if (response && response.status === "success" && response.data) {
        toast({
          title: "Account created!",
          description: "You have successfully registered.",
        });

        await new Promise((resolve) => setTimeout(resolve, 100));
        navigate("/dashboard");
      }
    } catch (error: any) {
      toast({
        title: "Registration failed",
        description:
          error.message || "There was a problem creating your account.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <AuthTransition>
      <div className="min-h-[calc(100vh-env(safe-area-inset-bottom,0px))] bg-slate-50 dark:bg-slate-900" style={{ minHeight: 'calc(var(--vh, 1vh) * 100)' }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,_rgb(148_163_184_/_0.15)_1px,_transparent_0)] [background-size:24px_24px]" />
        
        <div className="relative flex flex-col justify-center p-4 pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)]" style={{ minHeight: 'calc(var(--vh, 1vh) * 100)', paddingBottom: 'calc(var(--vh, 1vh) * 8)' }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mr-4 shadow-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                ManterFlow
              </h1>
            </div>
          </div>

          <div className="relative flex max-w-7xl mx-auto w-full bg-white dark:bg-slate-800 rounded-3xl shadow-xl overflow-hidden border border-slate-200 dark:border-slate-700">
            {/* Left: Branding */}
            <div className="hidden lg:flex flex-col justify-center w-2/5 bg-slate-50 dark:bg-slate-800/50 relative p-8">
              {/* Subtle decorative elements */}
              <div className="absolute top-10 right-10 w-24 h-24 bg-blue-50 dark:bg-blue-900/20 rounded-full opacity-60" />
              <div className="absolute bottom-10 left-10 w-20 h-20 bg-indigo-50 dark:bg-indigo-900/20 rounded-full opacity-40" />
              <div className="absolute top-1/2 right-6 w-12 h-12 bg-purple-50 dark:bg-purple-900/20 rounded-full opacity-30" />
              
              <div className="relative z-10">
                <div className="mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">Welcome to ManterFlow!</h2>
                  <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed">
                    Smart fee collection platform designed for modern organizations. Get started in minutes.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center text-slate-700 dark:text-slate-300">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
                      <BarChart3 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-sm">Automated billing & analytics</span>
                  </div>
                  <div className="flex items-center text-slate-700 dark:text-slate-300">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mr-3">
                      <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm">Multi-user & team support</span>
                  </div>
                  <div className="flex items-center text-slate-700 dark:text-slate-300">
                    <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mr-3">
                      <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <span className="text-sm">Enterprise-grade security</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Form */}
            <div className="flex-1 flex flex-col justify-start items-center p-6 sm:p-8 overflow-y-auto md:overflow-visible max-h-[80vh] md:max-h-none" style={{ maxHeight: 'calc(var(--vh, 1vh) * 80)' }}>
              <div className="w-full max-w-2xl">
                {/* <div className="lg:hidden text-center mb-6">
                  <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">Create your account</h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300">Sign up and set up your organization</p>
                </div> */}
                {/* <div className="hidden lg:block mb-6">
                  <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-1">Create your account</h2>
                  <p className="text-base text-slate-600 dark:text-slate-300">Sign up and set up your organization in one step.</p>
                </div>
                 */}
                {/* Google Sign Up */}
                <div className="mb-6">
                  <GoogleLoginButton mode="signup" label="Sign up with Google" />
                  <div className="flex items-center my-4">
                    <div className="flex-grow border-t border-slate-200 dark:border-slate-700" />
                    <span className="mx-2 text-slate-500 dark:text-slate-400 text-xs">or</span>
                    <div className="flex-grow border-t border-slate-200 dark:border-slate-700" />
                  </div>
                </div>
                
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3 pb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">Your Account</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 data-[invalid]:text-red-500">
                            Full Name
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <FormControl>
                              <Input
                                placeholder="Enter your full name"
                                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus-visible:ring-offset-0 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
                                {...field}
                                onChange={(e) =>
                                  handleInputChange(e, field.onChange)
                                }
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 data-[invalid]:text-red-500">
                            Email Address
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="Enter your email"
                                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus-visible:ring-offset-0 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
                                {...field}
                                onChange={(e) =>
                                  handleInputChange(e, field.onChange)
                                }
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 data-[invalid]:text-red-500">
                            Password
                          </FormLabel>
                          <div className="flex items-center gap-2 relative">
                            <Lock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <FormControl>
                              <Input
                                type={showPassword ? "text" : "password"}
                                placeholder="Create a password"
                                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus-visible:ring-offset-0 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
                                {...field}
                                onChange={(e) =>
                                  handleInputChange(e, field.onChange)
                                }
                              />
                            </FormControl>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="mobileNo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 data-[invalid]:text-red-500">
                            Mobile Number
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <FormControl>
                              <Input
                                type="tel"
                                placeholder="Enter mobile number"
                                pattern="[0-9]*"
                                maxLength={10}
                                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus-visible:ring-offset-0 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
                                onKeyDown={handleNumericInput}
                                {...field}
                                onChange={(e) =>
                                  handleInputChange(e, field.onChange)
                                }
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="orgName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-700 dark:text-slate-300 data-[invalid]:text-red-500">
                            Organization Name
                          </FormLabel>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <FormControl>
                              <Input
                                placeholder="Enter organization name"
                                className="bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 focus-visible:ring-offset-0 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400"
                                {...field}
                                onChange={(e) =>
                                  handleInputChange(e, field.onChange)
                                }
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                
                <div className="flex flex-col space-y-4 mt-6 col-span-full">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white shadow-lg transition-all duration-300 hover:shadow-blue-500/25" type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
            {/* Always visible sign-in link */}
            <div className="sticky bottom-0 bg-white dark:bg-slate-900 z-20 py-4 mt-6 mb-16 text-center text-sm text-slate-600 dark:text-slate-400 sm:static sm:mb-0">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-medium">
                Sign in
              </Link>
            </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthTransition>
  );
};

export default SignUp;
